import React, { useState } from 'react';

import { Icon } from 'design-system-web';
import cs from 'classnames';
import { useTranslation } from 'react-i18next';

import { VisitorType } from 'lane-shared/domains/visitorManagement/types/VisitorType';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';

import { Button, Thumbnail } from 'components/general';
import { H5, S } from 'components/typography';

import { isVisitorEmailConflict } from '../../helpers/isVisitorEmailConflict';

import styles from './VisitorListItem.scss';

type BulkVisitorsItemProps = {
  bulkVisitors: VisitorType[];
  onDelete?: (bulkVisitors: VisitorType[]) => void;
  submitterEmail?: string;
  hostEmail?: string;
};

const getBulkEmailConflictMessageKey = (
  hasHostEmailConflict: boolean,
  hasSubmitterEmailConflict: boolean
): string | null => {
  if (hasHostEmailConflict) {
    return 'web.content.feature.visitorManagement.visitorInvite.form.emailMatchesHostBulk';
  }

  if (hasSubmitterEmailConflict) {
    return 'web.content.feature.visitorManagement.visitorInvite.form.emailMatchesSubmitterBulk';
  }

  return null;
};

export function BulkVisitorsItem({
  bulkVisitors,
  onDelete,
  submitterEmail,
  hostEmail,
}: BulkVisitorsItemProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  const bulkVisitorsCount = bulkVisitors.length.toString();
  const hasSubmitterEmailConflict = bulkVisitors.some(visitor =>
    isVisitorEmailConflict(visitor.email, submitterEmail)
  );
  const hasHostEmailConflict = bulkVisitors.some(visitor =>
    isVisitorEmailConflict(visitor.email, hostEmail)
  );
  const emailConflictMessageKey = getBulkEmailConflictMessageKey(
    hasHostEmailConflict,
    hasSubmitterEmailConflict
  );
  const hasEmailConflict = Boolean(emailConflictMessageKey);

  const handleOnDelete = () => {
    if (onDelete) {
      onDelete(bulkVisitors);
    }
  };

  const renderDeleteButton = () => (
    <div>
      <Button
        testId="deleteBulkButtonTrigger"
        className={styles.button}
        size="small"
        onClick={() => setIsDeleting(true)}
        startIcon={
          <Icon
            name="trash-alt"
            testId="deleteIcon"
            set={ICON_SET_FONTAWESOME}
          />
        }
      >
        {t('Remove')}
      </Button>
    </div>
  );

  const renderDeleteActionButtons = () => (
    <>
      <div>
        <Button
          testId="deleteBulkButton"
          className={styles.button}
          size="small"
          variant="contained"
          onClick={handleOnDelete}
        >
          {t('web.content.feature.visitorManagement.visitorInvite.form.remove')}
        </Button>
      </div>
      <div>
        <Button
          testId="cancelDeleteBulkButton"
          className={styles.button}
          size="small"
          onClick={() => setIsDeleting(false)}
        >
          {t('web.content.feature.visitorManagement.visitorInvite.form.cancel')}
        </Button>
      </div>
    </>
  );

  return (
    <div
      className={cs(styles.visitorListItem, {
        [styles.warningItem]: hasEmailConflict,
      })}
    >
      <div className={styles.user}>
        <Thumbnail className={styles.thumbnail} name={bulkVisitorsCount} text />
        <div className={styles.textWrapper}>
          <H5>
            {t(
              'web.content.feature.visitorManagement.visitorInvite.form.bulkImportItem',
              { count: bulkVisitors.length }
            )}
          </H5>
          {emailConflictMessageKey && (
            <S className={styles.warningMessage} data-test="emailConflictStatus">
              {t(emailConflictMessageKey)}
            </S>
          )}
        </div>
      </div>
      {hasEmailConflict && (
        <div className={styles.warningIcon}>
          <Icon
            name="exclamation-circle"
            testId="exclamation-circle"
            set={ICON_SET_FONTAWESOME}
          />
        </div>
      )}
      {!isDeleting && renderDeleteButton()}
      {isDeleting && renderDeleteActionButtons()}
    </div>
  );
}
